import React, { useEffect, useRef, useState } from "react";
import api, { normalizeArray } from "../../apiClient";
import { createAppointment } from "../../api/appointmentsApi";
import "./QuickAddSheet.css";

const newRequestId = () =>
  (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
  `qa-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/* One-click add from any day/slot: click empty board space or a month day →
   this sheet (customer search, service, time) → done. Never a navigate-away
   form. Assignee rides in when the click came from a tech column. */
export default function QuickAddSheet({
  open,
  onClose,
  defaultDate = "",
  defaultTime = "09:00",
  assigneeUserId = null,
  assigneeName = "",
  onCreated,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [service, setService] = useState("");
  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState(defaultTime);
  const [hours, setHours] = useState("1");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const searchTimer = useRef(null);
  // Idempotency: one key per sheet-open — a retry after a false error returns
  // the SAME appointment instead of double-booking (one-entry-ever).
  const requestIdRef = useRef(newRequestId());
  const [serviceOptions, setServiceOptions] = useState([]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setResults([]);
      setCustomer(null);
      setService("");
      setDate(defaultDate);
      setTime(defaultTime || "09:00");
      setHours("1");
      setError("");
      requestIdRef.current = newRequestId();
    }
  }, [open, defaultDate, defaultTime]);

  // Tenant's real service types (FR-parity: FieldRoutes types for pest,
  // ServiceType rows otherwise) — a dropdown, not free text.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      const names = [];
      try {
        const { data } = await api.get("/scheduling/service-types/");
        for (const t of normalizeArray(data)) {
          const name = String(t.name || t.label || "").trim();
          if (name) names.push(name);
        }
      } catch { /* fall through */ }
      if (!names.length) {
        try {
          const { data } = await api.get("/operations/fieldroutes-service-types/");
          for (const t of normalizeArray(data)) {
            const name = String(t.description || t.name || "").trim();
            if (name) names.push(name);
          }
        } catch { /* free text fallback */ }
      }
      if (alive) setServiceOptions(Array.from(new Set(names)).sort());
    })();
    return () => {
      alive = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open || query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers/", { params: { search: query.trim(), page_size: 6 } });
        setResults(normalizeArray(data));
      } catch {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(searchTimer.current);
  }, [query, open]);

  if (!open) return null;

  const customerLabel = (c) =>
    `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || c.email || `#${c.id}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!customer || !date) return;
    setBusy(true);
    setError("");
    try {
      const start = new Date(`${date}T${time || "09:00"}:00`);
      await createAppointment({
        customer_id: String(customer.id),
        start: start.toISOString(),
        duration_minutes: Math.round(Number(hours || 1) * 60),
        assigned_user: assigneeUserId || null,
        service_type: service.trim() || "Service",
        client_request_id: requestIdRef.current,
      });
      onCreated?.();
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn’t create the appointment — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="qa-overlay" role="dialog" aria-modal="true" aria-label="Quick add appointment" onClick={onClose}>
      <form className="qa-sheet" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <header className="qa-head">
          <strong>Quick add</strong>
          <span className="qa-sub">
            {date || "Pick a day"}
            {assigneeName ? ` · ${assigneeName}` : ""}
          </span>
          <button type="button" className="qa-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </header>

        <div className="qa-field">
          {customer ? (
            <div className="qa-picked">
              <span>{customerLabel(customer)}</span>
              <button type="button" onClick={() => setCustomer(null)}>
                Change
              </button>
            </div>
          ) : (
            <>
              <input
                autoFocus
                type="text"
                placeholder="Search customers…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Customer search"
              />
              {results.length > 0 && (
                <ul className="qa-results">
                  {results.map((c) => (
                    <li key={c.id}>
                      <button type="button" onClick={() => setCustomer(c)}>
                        {customerLabel(c)}
                        {c.address ? <span> · {c.address}</span> : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        <div className="qa-row">
          {serviceOptions.length > 0 ? (
            <select value={service} onChange={(e) => setService(e.target.value)} aria-label="Service type">
              <option value="">Service type…</option>
              {serviceOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              placeholder="Service (e.g. Mow + edge)"
              value={service}
              onChange={(e) => setService(e.target.value)}
              aria-label="Service"
            />
          )}
        </div>
        <div className="qa-row qa-row--time">
          <label className="qa-labeled">
            <span>Date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Date" required />
          </label>
          <label className="qa-labeled">
            <span>Start</span>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} aria-label="Start time" />
          </label>
          <label className="qa-labeled">
            <span>Hours</span>
            <input
              type="number"
              min="0.5"
              max="12"
              step="0.5"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              aria-label="Duration in hours"
            />
          </label>
        </div>

        {error ? <div className="qa-error">{error}</div> : null}

        <button className="qa-submit" type="submit" disabled={busy || !customer || !date}>
          {busy ? "Adding…" : "Add appointment"}
        </button>
      </form>
    </div>
  );
}
